import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Calendar, AlertTriangle, FileText, ExternalLink, Loader2, RotateCcw, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import CancelSubscriptionModal from '../components/CancelSubscriptionModal';
import QuotaUsageCard from '../components/QuotaUsageCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDateFromUnix = (unix) => {
    if (!unix) return null;
    const d = new Date(unix * 1000);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatMoney = (amountInMinor, currency = 'usd') => {
    if (amountInMinor == null) return null;
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: (currency || 'usd').toUpperCase(),
        }).format(amountInMinor / 100);
    } catch {
        return `$${(amountInMinor / 100).toFixed(2)}`;
    }
};

export default function BillingPage() {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [data, setData] = useState(null);
    const [quotaData, setQuotaData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [reactivating, setReactivating] = useState(false);

    const fetchAccount = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [accRes, quotaRes] = await Promise.all([
                fetch(`${API}/subscriptions/account`, { credentials: 'include' }),
                fetch(`${API}/subscriptions/status`, { credentials: 'include' }),
            ]);
            if (accRes.ok) {
                setData(await accRes.json());
            } else {
                let detail = 'Could not load billing details';
                try {
                    const j = await accRes.json();
                    if (j?.detail) detail = j.detail;
                } catch (_) { /* ignore */ }
                setLoadError(detail);
            }
            if (quotaRes.ok) {
                setQuotaData(await quotaRes.json());
            }
        } catch (e) {
            console.error('Billing fetch error:', e);
            setLoadError('Network error - please try again');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (authLoading) return;
        if (!user) {
            navigate('/');
            return;
        }
        fetchAccount();
    }, [authLoading, user, navigate, fetchAccount]);

    const openStripePortal = async () => {
        try {
            const res = await fetch(`${API}/subscriptions/portal`, { credentials: 'include' });
            if (res.ok) {
                const d = await res.json();
                if (d.url) {
                    window.location.href = d.url;
                } else {
                    toast.info(d.message || 'Could not open Stripe portal');
                }
            } else {
                toast.error('Could not open payment portal');
            }
        } catch (e) {
            console.error(e);
            toast.error('Could not open payment portal');
        }
    };

    const handleReactivate = async () => {
        setReactivating(true);
        try {
            const res = await fetch(`${API}/subscriptions/reactivate`, {
                method: 'POST',
                credentials: 'include',
            });
            if (res.ok) {
                toast.success('Subscription reactivated - welcome back!');
                await fetchAccount();
            } else {
                toast.error('Could not reactivate subscription');
            }
        } catch (e) {
            console.error(e);
            toast.error('Could not reactivate subscription');
        } finally {
            setReactivating(false);
        }
    };

    if (loading || authLoading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center" data-testid="billing-loading">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (loadError || !data) {
        return (
            <div className="min-h-screen bg-background" data-testid="billing-error">
                <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
                    <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                        <button onClick={() => navigate('/dashboard')} className="p-2 rounded-lg hover:bg-secondary transition-colors">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <h1 className="text-lg font-semibold">Billing & Subscription</h1>
                    </div>
                </header>
                <main className="max-w-md mx-auto px-4 pt-16 text-center space-y-4">
                    <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
                    <h2 className="text-lg font-semibold">We couldn't load your billing details</h2>
                    <p className="text-sm text-muted-foreground">{loadError || 'Please try again in a moment.'}</p>
                    <div className="flex gap-2 justify-center">
                        <Button onClick={fetchAccount} data-testid="billing-retry-btn">Retry</Button>
                        <Button variant="outline" onClick={() => navigate('/dashboard')}>Back to dashboard</Button>
                    </div>
                </main>
            </div>
        );
    }

    // A user has a paid plan if their tier is anything but "free" - regardless
    // of whether we managed to link to Stripe (preview DBs may not have it).
    const isPaid = data.tier && data.tier !== 'free';
    const stripeLinked = !!data.has_stripe;
    const willEnd = stripeLinked && data.cancel_at_period_end && data.next_billing;
    const priceLabel = data.amount != null
        ? `${formatMoney(data.amount, data.currency)} / ${data.interval || 'month'}`
        : null;

    return (
        <div className="min-h-screen bg-background pb-16" data-testid="billing-page">
            {/* Header */}
            <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="p-2 rounded-lg hover:bg-secondary transition-colors"
                        data-testid="billing-back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-lg font-semibold">Billing & Subscription</h1>
                        <p className="text-xs text-muted-foreground">Manage your BrainSync plan</p>
                    </div>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 pt-6 space-y-4">
                {/* Cancellation banner */}
                {willEnd && (
                    <div className="flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50/70" data-testid="cancellation-banner">
                        <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-amber-900">
                                Your subscription will end on {formatDateFromUnix(data.next_billing)}
                            </p>
                            <p className="text-xs text-amber-700 mt-0.5">
                                You'll keep all Plus features until then. Change your mind?
                            </p>
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            className="bg-white"
                            onClick={handleReactivate}
                            disabled={reactivating}
                            data-testid="reactivate-btn"
                        >
                            {reactivating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                            Reactivate
                        </Button>
                    </div>
                )}

                {/* Current plan card */}
                <section className="rounded-2xl border border-border bg-card p-5" data-testid="current-plan-card">
                    <p className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase mb-2">Current plan</p>
                    <div className="flex items-end justify-between flex-wrap gap-3">
                        <div>
                            <h2 className="text-2xl font-bold">{data.plan_name}</h2>
                            {priceLabel && (
                                <p className="text-sm text-muted-foreground mt-1">{priceLabel}</p>
                            )}
                        </div>
                        {!isPaid ? (
                            <Button onClick={() => navigate('/dashboard?upgrade=1')} data-testid="upgrade-from-billing-btn">
                                Upgrade to Plus
                            </Button>
                        ) : null}
                    </div>

                    {/* When we couldn't reach Stripe (preview DB / admin grant), tell the user */}
                    {isPaid && !stripeLinked && (
                        <div className="mt-4 text-xs text-muted-foreground bg-secondary/40 rounded-lg px-3 py-2" data-testid="no-stripe-link-note">
                            Detailed billing info is unavailable in this environment. Visit billing on the live site to see your renewal date, payment method and invoices.
                        </div>
                    )}

                    <div className="mt-5 grid sm:grid-cols-2 gap-4">
                        {data.next_billing && (
                            <div className="flex items-center gap-3">
                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                <div>
                                    <p className="text-xs text-muted-foreground">{willEnd ? 'Ends on' : 'Renews on'}</p>
                                    <p className="text-sm font-medium">{formatDateFromUnix(data.next_billing)}</p>
                                </div>
                            </div>
                        )}
                        {data.card_last4 && (
                            <div className="flex items-center gap-3">
                                <CreditCard className="w-4 h-4 text-muted-foreground" />
                                <div>
                                    <p className="text-xs text-muted-foreground">Payment method</p>
                                    <p className="text-sm font-medium capitalize">{data.card_brand || 'Card'} •••• {data.card_last4}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* Usage this period */}
                {quotaData && (
                    <QuotaUsageCard data={quotaData} tier={data.tier} onUpgrade={() => navigate('/dashboard?upgrade=1')} />
                )}

                {/* Actions - only render when Stripe is linked (we need it for the portal + cancel) */}
                {isPaid && stripeLinked && (
                    <section className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid="billing-actions">
                        <button
                            onClick={openStripePortal}
                            className="w-full flex items-center justify-between p-4 hover:bg-secondary/40 transition-colors text-left"
                            data-testid="update-payment-method-btn"
                        >
                            <div className="flex items-center gap-3">
                                <CreditCard className="w-4 h-4 text-muted-foreground" />
                                <div>
                                    <p className="text-sm font-medium">Update payment method</p>
                                    <p className="text-xs text-muted-foreground">Securely change your card via Stripe</p>
                                </div>
                            </div>
                            <ExternalLink className="w-4 h-4 text-muted-foreground" />
                        </button>

                        <button
                            onClick={openStripePortal}
                            className="w-full flex items-center justify-between p-4 hover:bg-secondary/40 transition-colors text-left"
                            data-testid="manage-billing-info-btn"
                        >
                            <div className="flex items-center gap-3">
                                <FileText className="w-4 h-4 text-muted-foreground" />
                                <div>
                                    <p className="text-sm font-medium">Update billing info</p>
                                    <p className="text-xs text-muted-foreground">Address, name, tax ID</p>
                                </div>
                            </div>
                            <ExternalLink className="w-4 h-4 text-muted-foreground" />
                        </button>

                        {!willEnd && (
                            <button
                                onClick={() => setCancelModalOpen(true)}
                                className="w-full flex items-center justify-between p-4 hover:bg-red-50/50 transition-colors text-left"
                                data-testid="cancel-subscription-btn"
                            >
                                <div className="flex items-center gap-3">
                                    <AlertTriangle className="w-4 h-4 text-red-600" />
                                    <div>
                                        <p className="text-sm font-medium text-red-700">Cancel subscription</p>
                                        <p className="text-xs text-red-600/70">You'll keep access until your renewal date</p>
                                    </div>
                                </div>
                            </button>
                        )}
                    </section>
                )}

                {/* Invoice history */}
                {Array.isArray(data.invoices) && data.invoices.length > 0 && stripeLinked && (
                    <section className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="invoice-history">
                        <div className="px-5 pt-5 pb-3">
                            <p className="text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">Invoice history</p>
                        </div>
                        <div className="divide-y divide-border">
                            {data.invoices.map((inv) => (
                                <div key={inv.id} className="px-5 py-3 flex items-center justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium">
                                            {formatMoney(inv.amount_paid, inv.currency)}
                                            {inv.status === 'paid' && (
                                                <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5">
                                                    <CheckCircle2 className="w-2.5 h-2.5" /> Paid
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-xs text-muted-foreground">{formatDateFromUnix(inv.created)} {inv.number ? `· ${inv.number}` : ''}</p>
                                    </div>
                                    {inv.hosted_invoice_url && (
                                        <a
                                            href={inv.hosted_invoice_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-primary font-medium hover:underline"
                                            data-testid={`invoice-view-${inv.id}`}
                                        >
                                            View
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                <p className="text-[11px] text-muted-foreground text-center pt-2">
                    Payments are securely processed by Stripe. <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="underline">Privacy</a> · <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer" className="underline">Terms</a>
                </p>
            </main>

            <CancelSubscriptionModal
                open={cancelModalOpen}
                onClose={() => setCancelModalOpen(false)}
                planName={data.plan_name}
                priceLabel={priceLabel}
                renewsAt={formatDateFromUnix(data.next_billing)}
                onCancelled={() => {
                    setCancelModalOpen(false);
                    fetchAccount();
                }}
            />
        </div>
    );
}
